import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Link from "@mui/material/Link";





const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: '#fff',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 480,
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  card: {
    width: '100%',
    background: '#fff',
    boxShadow: 'none',
    margin: 0,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: 260,
    objectFit: 'contain',
    display: 'block',
    border: 'none',
  },
  restaurantInfo: {
    padding: theme.spacing(2, 3),
    textAlign: 'left',
  },
  restaurantTitle: {
    fontWeight: 700,
    fontSize: '1.4rem',
    marginBottom: 2,
    color: '#000',
    lineHeight: 1.2,
  },
  restaurantSubtitle: {
    color: '#666',
    fontSize: '0.95rem',
    marginBottom: theme.spacing(2),
    lineHeight: 1.2,
  },
  buttonRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  actionButton: {
    flex: 1,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: '0.95rem',
    padding: theme.spacing(1.2, 0),
    textTransform: 'none',
    boxShadow: 'none',
    height: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#000',
    color: '#fff',
    '& svg': {
      marginRight: 6,
      fontSize: 18,
    },
  },
  promoButton: {
    background: '#f9e1e8',
    color: '#e55c8a',
    '&:hover': {
      background: '#f7d5df',
    },
    '& svg': {
      color: '#e55c8a',
    },
  },
  section: {
    background: '#f5f5f5',
    padding: theme.spacing(0, 3, 2),
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
  },
  obLogo: {
    width: 70,
    height: 70,
    margin: `${theme.spacing(1.5)}px auto`,
    display: 'block',
    borderRadius: 16,
    background: '#000',
  },
  obLogoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: 16,
  },
  appTitle: {
    fontWeight: 800,
    fontSize: '1.1rem',
    margin: theme.spacing(1, 0, 0.5, 0),
    color: '#000',
    textAlign: 'center',
    lineHeight: 1.2,
  },
  appTitleHighlight: {
    color: '#e55c8a',
    fontWeight: 800,
  },
  bulletList: {
    textAlign: 'left',
    margin: theme.spacing(2, 0),
    paddingLeft: 0,
    listStyle: 'none',
  },
  bulletItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.95rem',
    marginBottom: theme.spacing(1),
    color: '#000',
    lineHeight: 1.2,
  },
  bulletIcon: {
    color: '#e55c8a',
    marginRight: theme.spacing(1),
    fontSize: 20,
  },
  storeRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  storeBtn: {
    height: 44,
    objectFit: 'contain',
  },
  appStoreContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  storeLinksContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
  storeLink: {
    display: 'inline-block',
  },
  appleImage: {
    height: 55,
    objectFit: 'contain',
  },
  googleImage: {
    height: 55,
    objectFit: 'contain',
  },
  pageDot: {
    width: 40,
    height: 4,
    borderRadius: 2,
    background: '#000',
    margin: '0 auto',
    marginTop: theme.spacing(2),
  }
}));

const NotFound = () => {
  const classes = useStyles();
  
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isChinese, setIsChinese] = useState(false);
  
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    setIsIOS(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
    setIsAndroid(/android/i.test(userAgent));
    
    // Detect Chinese language preference
    const language = navigator.language || navigator.userLanguage;
    setIsChinese(language.toLowerCase().includes('zh'));
  }, []);

  const renderAppStoreIcons = () => {
    return (
      <Box className={classes.appStoreContainer}>
        <div className={classes.storeLinksContainer}>
          {(isIOS || (!isIOS && !isAndroid)) && (
            <Link 
              href="https://apps.apple.com/us/app/orderbuddy-app/id1599842262"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.storeLink}
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=180x60"
                alt="Download on the App Store"
                className={classes.appleImage}
              />
            </Link>
          )}
          
          {(isAndroid || (!isIOS && !isAndroid)) && (
            <Link 
              href="https://play.google.com/store/apps/details?id=au.com.orderbuddy.orderbuddyapp"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.storeLink}
            >
              <img
                alt="Get it on Google Play"
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                className={classes.googleImage}
              />
            </Link>
          )}
        </div>
      </Box>
    );
  };

  // Define text content based on language
  const content = {
    appTitle: isChinese ? (
      <>ORDERBUDDY APP<br />一站式美食优惠</>
    ) : (
      <>EXCLUSIVE DISCOUNTS<br />ON <span className={classes.appTitleHighlight}>ORDERBUDDY APP</span></>
    ),
    downloadSignup: isChinese ? "立刻下载 APP 和注册！" : "DOWNLOAD AND SIGN UP NOW!",
    bulletPoints: isChinese ? [
      "立刻享受折扣和更多优惠",
      "轻松查看订单（历史或当前）"
    ] : [
      "Enjoy immediate exclusive discounts on your order",
      "Easily view your orders (past or current)"
    ]
  };

  return (
    <Box className={classes.root}>

      <Box className={classes.section}>
        <Typography className={classes.appTitle}>
          {content.appTitle}
        </Typography>
        <div className={classes.obLogo}>
          <img src="/images/ob-logo-small.png" alt="Orderbuddy App" className={classes.obLogoImg} />
        </div>
        <Typography variant="h6" style={{ fontWeight: 700, margin: '8px 0 4px 0', fontSize: '1rem' }}>
          {content.downloadSignup}
        </Typography>
        <ul className={classes.bulletList}>
          {content.bulletPoints.map((point, index) => (
            <li key={index} className={classes.bulletItem}>
              <CheckCircleIcon className={classes.bulletIcon} />
              {point}
            </li>
          ))}
        </ul>
        <div className={classes.storeRow}>
          {renderAppStoreIcons()}
        </div>
      </Box>
    </Box>
  );
};

export default NotFound; 